/**
 * Vehicle trade data resume section
 *
 * @author Fabio Dal Santo <fabio@gestionaleauto.com>
 */

/**
 * Main
 */
import React, { useEffect } from 'react';
import HttpStatus from 'http-status-codes';
import PropTypes from 'prop-types';
/**
 * Material UI
 */
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
/**
 * Redux
 */
import { useSelector, useDispatch } from 'react-redux';
import { leadState, updateQuoteData, updateQuoteStepsValidation } from '../../../redux/leadSlice';
import {
  failUpdate, initUpdate, setSubStep, setStep, successUpdate, wizardState,
} from '../../../redux/wizardSlice';
import { lookupsState } from '../../../redux/lookupsSlice';
/**
 * Helpers
 */
import formatters from '../../../helpers/formatters';
import Api from '../../../helpers/api';
import { steps } from '../../../helpers/const';
import { getToken } from '../../../helpers/auth';
/**
 * Components
 */
import DocumentsUpload from '../DocumentsUpload';
import CallToAction from '../../../components/CallToAction';

const useStyles = makeStyles((theme) => ({
  resumeData: {
    borderRadius: 16,
    marginBottom: 22,
    padding: '25px 5px 20px 5px',
    textAlign: 'center',
  },
  resumeMakeModel: {
    fontWeight: 'bold',
    display: 'inline-block',
    width: '100%',
    maxWidth: 330,
  },
  resumeMake: {
    fontSize: '1.46em',
    lineHeight: '1.32em',
    display: 'block',
    marginBottom: 5,
  },
  resumeModel: {
    fontSize: '1.06em',
    lineHeight: '1.62em',
    letterSpacing: 0.48,
    display: 'block',
    marginBottom: 3,
  },
  resumeTech: {
    borderTop: '2px solid',
    margin: '0 auto',
    width: '100%',
    maxWidth: 330,
    justifyContent: 'center',
    display: 'flex',
    paddingTop: 3,
  },
  resumeTechItem: {
    flexGrow: 1,
    lineHeight: 1.73,
  },
  resumePrice: {
    fontSize: '2em',
    lineHeight: '1.32em',
    display: 'inline-block',
    width: '100%',
    fontWeight: 'bold',
    margin: '25px 0',
    color: theme.palette.primary.main,
  },
  resumeMsg: {
    color: theme.palette.primary.main,
    fontSize: '1.26em',
    margin: '25px 0',
    paddingLeft: 40,
    paddingRight: 40,
    display: 'inline-block',
    width: '78%',
  },
  resumeDisclaimer: {
    fontSize: '0.8em',
    margin: '0px 40px 40px 40px',
    color: theme.palette.secondary.contrastText,
    textAlign: 'center',
  },
  resumeUpdate: {
    borderRadius: 9,
  },
}));

/**
 *
 * @param readOnly: bool Remove update action
 * @param updateOnStart: bool Cpdate trade value on start
 * @param completeLead: bool Complete Lead on start
 * @returns {JSX.Element}
 * @constructor
 */
function Resume({ readOnly, updateOnStart, completeLead }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { cookie, quote, leadId } = useSelector(leadState);
  const { actionRequired } = useSelector(wizardState);
  const { configuration } = useSelector(lookupsState);
  const disclaimerText = t('The average sales prices indicated above refer to restored cars offered by professional dealers and accompanied by a guarantee required by law. If you want to sell your car, don\'t forget that the pick-up value of your used car will probably be lower than the online sale price as the restoration costs and legal warranty costs will have to be reduced, which will be borne by the dealer who wants to put it on sale.');
  const noPriceText = t('We are sorry, the data provided does not allow for a correct evaluation of the vehicle. We will contact you for a personalized evaluation.');
  // Check if quotation is empty
  const data = Object.entries(quote).length === 1 ? cookie : quote;
  const {
    makeText: make,
    modelText: model,
    stateRegistrationYear: registrationYear,
    stateMileage: mileage,
    techColor: color,
    versionId,
    versionText,
    versionCustom,
  } = data;
  const quoteValue = configuration?.financial?.trade !== 'active-without' ? data.quoteValue : 0;
  const version = (versionId !== '0' ? versionText : versionCustom)?.replace(model, '').trim();

  const completeQuotation = async () => {
    const res = await Api.post({
      entryPoint: `/lead/${leadId}/complete`,
      headers: { 'ga-token': getToken(leadId) },
      silentFail: true,
    });
    if (res.status === HttpStatus.OK) {
      if (window?.quotationData?.thank_you_url) {
        const thankYouUrl = window.quotationData.thank_you_url;
        setTimeout(() => {
          window.location.replace(window.location.origin + thankYouUrl);
        }, 5000);
      }
    }
  };

  const submitForm = async (throwDispatch = true) => {
    const tradeData = {
      make,
      model,
      reg_year: registrationYear,
      fuel: quote.techFuelText,
      body: quote.techBodyText,
      doors: formatters.toNumber(quote.techDoors),
      gearbox: quote.techGearboxText,
      displacement: formatters.toNumber(quote.techDisplacement),
      power: formatters.toNumber(quote.techPower),
      mileage: formatters.toNumber(quote.stateMileage),
      owners: formatters.toNumber(quote.statePreviousOwners),
      color,
      maintenance_book: Boolean(quote.stateMaintenanceBook),
      quote_value: formatters.toNumber(quoteValue),
      version: versionId !== '0' ? versionText : versionCustom,
      infocar_code: formatters.toNumber(versionId),
    };
    const res = await Api.patch({
      entryPoint: `/lead/${leadId}/trade`,
      headers: { 'ga-token': getToken(leadId) },
      data: tradeData,
    });
    if (throwDispatch) {
      if (res.status !== HttpStatus.OK) {
        dispatch(failUpdate());
        return;
      }
      dispatch(successUpdate());
    }
  };
  useEffect(() => {
    if (updateOnStart) {
      submitForm(false);
    }
    if (completeLead) {
      completeQuotation();
    }
  }, []);
  useEffect(() => {
    if (actionRequired) {
      dispatch(initUpdate());
      submitForm();
    }
  }, [actionRequired]);

  return (
    <>
      <div className={`${classes.resumeData} ga-theme-form-bg ga-quotation-data-resume`}>
        <div className={`${classes.resumeMakeModel} ga-quotation-data-resume-vehicle`}>
          <span className={`${classes.resumeMake} ga-quotation-data-resume-make`}>{`${make} ${model}`}</span>
          <span className={`${classes.resumeModel} ga-quotation-data-resume-version`}>{version}</span>
        </div>
        <div className={`${classes.resumeTech} ga-quotation-data-resume-tech`}>
          <div className={`${classes.resumeTechItem} ga-quotation-data-resume-item mileage`}>
            {`${t('Km')}: ${formatters.number(mileage, false)}`}
          </div>
          <div className={`${classes.resumeTechItem} ga-quotation-data-resume-item year`}>
            {`${t('Year')}: ${registrationYear}`}
          </div>
          <div className={`${classes.resumeTechItem} ga-quotation-data-resume-item color`}>
            {`${t('Color')}: ${color && color !== '' ? color : t('n/a')}`}
          </div>
        </div>
        {quoteValue > 0 && (
          <>
            <span className={`${classes.resumePrice} ga-quotation-data-resume-price`}>
              {`${formatters.number(quoteValue)} €`}
            </span>
            <div className={`${classes.resumeDisclaimer} ga-quotation-data-resume-disclaimer`}>
              {disclaimerText}
            </div>
          </>
        )}
        {quoteValue === 0 && (
          <span className={`${classes.resumeMsg} ga-quotation-data-resume-msg`}>
            {noPriceText}
          </span>
        )}
        { !readOnly && (
          <button
            type="button"
            onClick={() => {
              if (Object.entries(quote).length === 0) {
                // Update requested, if necessary copy cookie data
                // into user data to make them customizable
                dispatch(updateQuoteData({ ...cookie }));
                dispatch(updateQuoteStepsValidation({ isValid: true }));
              } else {
                // Return to initial step
                dispatch(setStep(steps.trade));
                dispatch(setSubStep(0));
              }
            }}
            className={`${classes.resumeUpdate} ga-theme-button-update`}
          >
            {t('Update requested')}
          </button>
        )}
      </div>
      { !readOnly && (
        <div className="col-xs-12">
          <DocumentsUpload template="smart" />
        </div>
      ) }
      { readOnly && <CallToAction /> }

    </>
  );
}
Resume.defaultProps = {
  readOnly: false,
  updateOnStart: false,
  completeLead: false,
};
Resume.propTypes = {
  readOnly: PropTypes.bool,
  updateOnStart: PropTypes.bool,
  completeLead: PropTypes.bool,
};
export default Resume;
